import React, { useEffect, useMemo, useRef, useState, useCallback } from "react";
import {
  Button,
  TextField,
  Stack,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  Typography,
  Box,
  CircularProgress,
} from "@mui/material";
import * as Yup from "yup";
import { Formik, Form } from "formik";
import { useDispatch, useSelector } from "react-redux";
import MasterViewController from "../../redux/actions/masterApiActions";
import HolidayViewController from "../../redux/actions/HolidayApiAction";
import FormDrawer from "../../components/layout/FormDrawer";
import ReusableGrid from "../../components/ag grid/ReusableGrid";
import {
  BRAND,
  inputSx,
  primaryButtonSx,
} from "../../components/auth/authTheme";
import { getUserInfoFromToken } from "../../utils/authUtils";

const validationSchema = Yup.object({
  occasion: Yup.string().required("Occasion is required"),
  date: Yup.date().required("Date is required"),
  year: Yup.number()
    .typeError("Year must be a number")
    .required("Year is required"),
});

function HolidayMasterFormModal({
  show,
  onHide,
  selectedHolidays,
  setSelectedHolidays,
  initialData = null,
}) {
  const dispatch = useDispatch();
  const selectionGridRef = useRef(null);
  const isApplyingSelection = useRef(false);
  const { addHolidays, updateHoliday } = MasterViewController();
  const { addEmployerHoliday } = HolidayViewController();
  const { holidays, isLoding: masterLoading } = useSelector(
    (state) => state.master
  );
  const { isLoding: holidayLoading } = useSelector(
    (state) => state.employer_holidays
  );

  const [userType, setUserType] = useState(null);
  const [selectedYear, setSelectedYear] = useState(new Date().getFullYear());
  const [submitting, setSubmitting] = useState(false);

  const isEdit = Boolean(initialData?.id);
  const isEmployer = userType && userType !== "Admin";

  useEffect(() => {
    const { userType: type } = getUserInfoFromToken();
    setUserType(type);
  }, []);

  const availableYears = useMemo(() => {
    const yrs = Object.values(holidays || {})
      .flat()
      .map((h) => h.year)
      .filter(Boolean);
    const current = new Date().getFullYear();
    return Array.from(new Set([current, ...yrs])).sort((a, b) => b - a);
  }, [holidays]);

  const filteredHolidays = useMemo(
    () =>
      Object.values(holidays || {})
        .flat()
        .filter((h) => h.year === selectedYear),
    [holidays, selectedYear]
  );

  const syncGridSelection = useCallback(() => {
    const api = selectionGridRef.current;
    if (!api || !show) return;

    isApplyingSelection.current = true;
    api.forEachNode((node) => {
      if (node.data?.id != null) {
        node.setSelected(selectedHolidays.includes(node.data.id));
      }
    });
    isApplyingSelection.current = false;
  }, [show, selectedHolidays]);

  useEffect(() => {
    if (!show || !isEmployer) return;
    syncGridSelection();
  }, [show, isEmployer, selectedYear, filteredHolidays, syncGridSelection]);

  const handleSelectionChanged = useCallback(
    (rows) => {
      if (isApplyingSelection.current) return;

      setSelectedHolidays((prev) => {
        const yearIds = new Set(filteredHolidays.map((h) => h.id));
        const otherYears = prev.filter((id) => !yearIds.has(id));
        return [...otherYears, ...rows.map((row) => row.id)];
      });
    },
    [filteredHolidays, setSelectedHolidays]
  );

  const initialValues = {
    occasion: initialData?.occasion || "",
    date: initialData?.date || "",
    year: initialData?.year || new Date().getFullYear(),
  };

  const handleAdminSubmit = async (
    values,
    { resetForm, setSubmitting: setFormSubmitting }
  ) => {
    setSubmitting(true);
    try {
      const action = isEdit
        ? updateHoliday(initialData.id, values)
        : addHolidays(values);
      await dispatch(action);
      resetForm();
      onHide();
    } catch (err) {
      console.error("Error submitting holiday:", err);
    } finally {
      setSubmitting(false);
      setFormSubmitting(false);
    }
  };

  const handleEmployerSubmit = async () => {
    const allHolidays = Object.values(holidays || {}).flat();
    const selected = allHolidays.filter((holiday) =>
      selectedHolidays.includes(holiday.id)
    );

    if (!selected.length) {
      return;
    }

    setSubmitting(true);
    try {
      await dispatch(addEmployerHoliday(selected));
      setSelectedHolidays([]);
      onHide();
    } catch (err) {
      console.error("Error adding employer holidays:", err);
    } finally {
      setSubmitting(false);
    }
  };

  const selectionColumnDefs = useMemo(
    () => [
      {
        headerCheckboxSelection: true,
        checkboxSelection: true,
        width: 52,
        maxWidth: 52,
        minWidth: 52,
        pinned: "left",
        suppressSizeToFit: true,
        sortable: false,
        headerClass: "checkbox-selection-header",
        cellClass: "checkbox-selection-cell",
      },
      {
        headerName: "Occasion",
        field: "occasion",
        flex: 2,
        minWidth: 200,
        wrapText: true,
        autoHeight: true,
        cellClass: "ag-cell-wrap-text",
        tooltipField: "occasion",
      },
      {
        headerName: "Date",
        field: "date",
        width: 118,
        maxWidth: 118,
        minWidth: 118,
        suppressSizeToFit: true,
        sortable: true,
        cellClass: "ag-cell-ellipsis",
      },
      {
        headerName: "Day",
        field: "day",
        width: 96,
        maxWidth: 96,
        minWidth: 96,
        suppressSizeToFit: true,
        sortable: true,
        cellClass: "ag-cell-ellipsis",
      },
    ],
    []
  );

  const isSaving = submitting || masterLoading || holidayLoading;

  if (isEmployer) {
    return (
      <FormDrawer
        open={show}
        onClose={onHide}
        title="Select Holidays"
        subtitle="Choose holidays from the master list for your organization"
        width={{ xs: "100%", sm: 640, md: 760 }}
        footer={
          <>
            <Typography variant="body2" sx={{ color: BRAND.muted, mr: "auto" }}>
              {selectedHolidays.length} selected
            </Typography>
            <Button onClick={onHide} color="inherit" disabled={isSaving}>
              Cancel
            </Button>
            <Button
              type="button"
              variant="contained"
              color="primary"
              disabled={isSaving || selectedHolidays.length === 0}
              onClick={handleEmployerSubmit}
              sx={{
                ...primaryButtonSx,
                minWidth: 140,
                "&.Mui-disabled": {
                  bgcolor: isSaving ? BRAND.orange : undefined,
                  color: isSaving ? "#fff" : undefined,
                  opacity: isSaving ? 0.85 : undefined,
                },
              }}
              startIcon={
                isSaving ? <CircularProgress size={18} color="inherit" /> : null
              }
            >
              {isSaving ? "Saving..." : "Save Selection"}
            </Button>
          </>
        }
      >
        <Typography variant="body2" sx={{ color: BRAND.muted, mb: 1.5 }}>
          Check the holidays your organization observes. Selections are saved across years.
        </Typography>

        <Box sx={{ mb: 2 }}>
          <FormControl size="small" fullWidth sx={inputSx}>
            <InputLabel id="select-holiday-year-label">Year</InputLabel>
            <Select
              labelId="select-holiday-year-label"
              value={selectedYear}
              label="Year"
              onChange={(e) => setSelectedYear(Number(e.target.value))}
            >
              {availableYears.map((year) => (
                <MenuItem key={year} value={year}>
                  {year}
                </MenuItem>
              ))}
            </Select>
          </FormControl>
        </Box>

        <Box sx={{ height: "calc(100vh - 300px)", minHeight: 360 }}>
          <ReusableGrid
            rowData={filteredHolidays}
            columnDefs={selectionColumnDefs}
            gridRef={selectionGridRef}
            pagination={true}
            pageSize={10}
            autoRowHeight={true}
            getRowId={(params) => String(params.data.id)}
            onGridReady={() => {
              requestAnimationFrame(syncGridSelection);
            }}
            onSelectionChanged={handleSelectionChanged}
            emptyMessage="No holidays for this year"
            emptyHint="Try selecting a different year from the dropdown above."
          />
        </Box>
      </FormDrawer>
    );
  }

  return (
    <Formik
      initialValues={initialValues}
      validationSchema={validationSchema}
      onSubmit={handleAdminSubmit}
      enableReinitialize
      validateOnBlur
      validateOnChange={false}
    >
      {({
        values,
        handleChange,
        touched,
        errors,
        isSubmitting,
        submitForm,
      }) => {
        const adminSaving = isSubmitting || submitting || masterLoading;

        return (
          <Form style={{ display: "contents" }}>
            <FormDrawer
              open={show}
              onClose={onHide}
              title={isEdit ? "Edit Holiday" : "Add Holiday"}
              subtitle={
                isEdit
                  ? "Update holiday master details"
                  : "Add a new holiday to the master list"
              }
              footer={
                <>
                  <Button onClick={onHide} color="inherit" disabled={adminSaving}>
                    Cancel
                  </Button>
                  <Button
                    type="button"
                    variant="contained"
                    color="primary"
                    disabled={adminSaving}
                    onClick={() => submitForm()}
                    sx={{
                      ...primaryButtonSx,
                      minWidth: 120,
                      "&.Mui-disabled": {
                        bgcolor: adminSaving ? BRAND.orange : undefined,
                        color: adminSaving ? "#fff" : undefined,
                        opacity: adminSaving ? 0.85 : undefined,
                      },
                    }}
                    startIcon={
                      adminSaving ? (
                        <CircularProgress size={18} color="inherit" />
                      ) : null
                    }
                  >
                    {adminSaving ? "Saving..." : isEdit ? "Update" : "Create"}
                  </Button>
                </>
              }
            >
              <Stack spacing={2.5}>
                <TextField
                  label="Occasion"
                  name="occasion"
                  value={values.occasion}
                  onChange={handleChange}
                  error={touched.occasion && Boolean(errors.occasion)}
                  helperText={touched.occasion && errors.occasion}
                  fullWidth
                  required
                  sx={inputSx}
                />
                <TextField
                  type="date"
                  label="Date"
                  name="date"
                  InputLabelProps={{ shrink: true }}
                  value={values.date}
                  onChange={handleChange}
                  error={touched.date && Boolean(errors.date)}
                  helperText={touched.date && errors.date}
                  fullWidth
                  required
                  sx={inputSx}
                />
                <TextField
                  label="Year"
                  name="year"
                  type="number"
                  value={values.year}
                  onChange={handleChange}
                  error={touched.year && Boolean(errors.year)}
                  helperText={touched.year && errors.year}
                  fullWidth
                  required
                  sx={inputSx}
                />
              </Stack>
            </FormDrawer>
          </Form>
        );
      }}
    </Formik>
  );
}

export default HolidayMasterFormModal;
